/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, Fragment, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { Modal, Button, ModalContent, ModalFooter, ModalHeader } from '@forge/common';

import Toast from '../../reusablecomponents/toast';

import { useRevokeAuthenticityMutation } from '../../services/api';

import { LOCALE_CONST } from '../../utils/constants';
import { gatewayRevokeErrMsg } from '../../utils/utility';

import { RevokeModalProps, ToastTypeProps } from '../../models';

import './index.scss';

const RevokeAuthModal: React.FC<RevokeModalProps> = ({
	isOpen, onClose, deviceId, deviceName, refetchDeviceList
}) => {
	const { t } = useTranslation();

	const convertFn = (value: string) => t(value);

	const [showToast, setShowToast] = useState<boolean>(false);

	const [toastContent, setToastContent] = useState<string>('');
	const [toastType, setToastType] = useState<ToastTypeProps>('success');

	const [revokeDevice] = useRevokeAuthenticityMutation();

	const displayToast = (type: ToastTypeProps, content: string) => {
		setToastType(type);
		setToastContent(content);

		if (type === "success") {
			refetchDeviceList();
		}

		setShowToast(true);
	};

	const handleRevoke = () => {
		onClose();

		revokeDevice({ deviceId })
			.then((response: any) => {
				let content: string = "";
				let type: ToastTypeProps = "success";

				if ('data' in response) {
					content = t(LOCALE_CONST.revokeSuccess, { gatewayName: deviceName });
				} else if ('error' in response) {
					type = "error";
					content = gatewayRevokeErrMsg(convertFn, t(LOCALE_CONST.revokeError, { gatewayName: deviceName }), response?.error?.status);
				}
 
				displayToast(type, content);
			}).catch((error: any) => {
				let content: string = gatewayRevokeErrMsg(convertFn, t(LOCALE_CONST.revokeErrorText), error?.error?.status);
				displayToast("error", content);
			});
	};

	useEffect(() => {
		if (showToast) {
			const TIME_OUT = 3000;

			const timer = setTimeout(() => {
				setShowToast(false);
			}, TIME_OUT);

			return () => clearTimeout(timer);
		}
	}, [showToast]);

	const renderConfirmationText = () => <div>{t(LOCALE_CONST.revokeConfirmation, { gatewayName: deviceName })}</div>;

	const renderRevokeModal = () => {
		return (
			<Modal onModalClose={onClose} onBackdropClick={onClose} className='revoke-modal'>
				<ModalHeader title={t(LOCALE_CONST.revokeGateway)} />
				<ModalContent padded>
					<div>
						{renderConfirmationText()}
					</div>
				</ModalContent>
				<ModalFooter
					rightSlot={
						<div className="revoke-modal-footer">
							<Button onClick={onClose} variant="secondary" data-testid="cancel-button-test-id">{t(LOCALE_CONST.cancel)}</Button>
							<Button onClick={handleRevoke} variant="primary"
								data-testid="revoke-button-test-id" 
							>
								{t(LOCALE_CONST.revoke)}
							</Button>
						</div>
					}
				/>
			</Modal>
		);
	};

	const renderToastComp = () => {
		return (
			<div className="toast-container">
				<Toast id="revoke-toast" showClose={false} showDismiss={false}
					title={toastContent} type={toastType}
					lifetime={3000} subtitle={''} content={''} />
			</div>
		);
	};

	return (
		<Fragment>
			{isOpen && renderRevokeModal()}

			{showToast && renderToastComp()}
		</Fragment>
	);
};

export default RevokeAuthModal;